import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import SyncReportsDashboard from './components/SyncReportsDashboard';
import CronJobMonitoringDashboard from './components/CronJobMonitoringDashboard';
const OrganizationDetailView = lazy(() => import('./components/OrganizationDetailView'));
import ResetPassword from './auth/ResetPassword';
import ForgotPassword from './auth/ForgotPassword';
import PasswordSetup from './auth/PasswordSetup';
const LoginPage = lazy(() => import('./auth/LoginPage'));
const OrgDashboard = lazy(() => import('./dashboard/OrgDashboard'));
const AdminDashboard = lazy(() => import('./dashboard/AdminDashboard'));
const UserFormWrapper = lazy(() => import('./dashboard/UserFormWrapper'));
const OrgProfileWrapper = lazy(() => import('./dashboard/OrgProfileWrapper'));
const QueueManagementDashboard = lazy(() => import('./pages/QueueManagement'));
const DailySyncReport = lazy(() => import('./pages/DailySyncReport'));
const EmailTemplates = lazy(() => import('./dashboard/EmailTemplates'));

const Loading = () => (
    <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

interface ProtectedRouteProps { roles: string[] }

const ProtectedRoute = ({ roles }: ProtectedRouteProps) => {
    const token = localStorage.getItem('token');
    const role = (localStorage.getItem('role') || '').toUpperCase(); // normalize case
    const allowedRoles = roles.map(r => r.toUpperCase());

    if (!token || !role || !allowedRoles.includes(role)) {
        return <Navigate to="/login" replace />;
    }
    return <Outlet />;
};

const PublicRoute = () => {
    const token = localStorage.getItem('token');
    const role = (localStorage.getItem('role') || '').toUpperCase();

    if (token && role) {
        if (role === 'ADMIN') return <Navigate to="/admin-user" replace />;
        if (role === 'ORGANIZATION') return <Navigate to="/org" replace />;
    }
    return <Outlet />;
};

export default function AppRouter() {
    // Root redirector: decide destination based on auth state
    const RootRedirect = () => {
        const token = localStorage.getItem('token');
        const role = (localStorage.getItem('role') || '').toUpperCase();
        if (token && role) {
            if (role === 'ADMIN') return <Navigate to="/admin-user" replace />;
            if (role === 'ORGANIZATION') return <Navigate to="/org" replace />;
        }
        return <Navigate to="/login" replace />;
    };

    return (
        <Suspense fallback={<Loading />}>
            <Routes>
                {/* Root: redirect based on login state */}
                <Route path="/" element={<RootRedirect />} />

                <Route element={<PublicRoute />}>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path='forgot-password' element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/setup-password" element={<PasswordSetup />} />
                </Route>

                <Route element={<ProtectedRoute roles={['ORGANIZATION']} />}>
                    <Route path="/org" element={<OrgDashboard />} />
                    <Route path="/org/profile/edit" element={<OrgProfileWrapper />} />
                </Route>

                <Route element={<ProtectedRoute roles={['ADMIN']} />}>
                    <Route path="/admin-user" element={<AdminDashboard />} />
                    <Route path="/admin-user/users/new" element={<UserFormWrapper />} />
                    <Route path="/admin-user/users/:id" element={<UserFormWrapper />} />
                    <Route path="/cron-monitoring" element={<SyncReportsDashboard />} />
                    <Route path="/cron-job-monitoring" element={<CronJobMonitoringDashboard />} />
                    <Route path="/daily-sync-report" element={<DailySyncReport />} />
                    <Route path="/email-templates" element={<EmailTemplates />} />
                </Route>


                <Route element={<ProtectedRoute roles={['ADMIN', 'ORGANIZATION']} />}>
                    {/* <Route path="/queue-monitoring" element={<QueueMonitoring />} /> */}
                    <Route path="/queue-monitoring" element={<QueueManagementDashboard />} />
                    <Route path="/queue-management" element={<QueueManagementDashboard />} />
                    <Route path="/organization/:orgId" element={<OrganizationDetailView />} />
                </Route>

                <Route path="/unauthorized" element={<div>Unauthorized</div>} />
                <Route path="*" element={<div>404 Not Found</div>} />
            </Routes>
        </Suspense>
    );
}
